import { useEffect, useRef } from "react";
import { X } from "lucide-react";

import { SidebarContents } from "@/components/navigation/Sidebar";
import { cn } from "@/lib/utils";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
}

/** Slide-in nav drawer for screens < lg. Mirrors the desktop sidebar
 *  contents and traps focus inside the panel while open. */
export function MobileNav({ open, onClose, isOpen, toggle }: MobileNavProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape, lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-[900] lg:hidden",
        open ? "pointer-events-auto" : "pointer-events-none",
      )}
      aria-hidden={!open}
    >
      <div
        className={cn(
          "absolute inset-0 bg-navy/60 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Primary navigation"
        className={cn(
          "absolute left-0 top-0 flex h-full w-sidebar flex-col bg-navy text-white shadow-modal transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-btn text-white/70 hover:bg-white/10 hover:text-white"
        >
          <X size={18} strokeWidth={1.5} aria-hidden="true" />
        </button>
        <SidebarContents
          compact={false}
          isOpen={isOpen}
          toggle={toggle}
          onNavigate={onClose}
        />
      </aside>
    </div>
  );
}
